/**
 * Created by Ning Jiang on 5/18/2017.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {ReelSymbolName} from "@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolName";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {TimelineLite, TweenLite} from "gsap";
import {WixxReelSymbol} from "../reelsymbol/WixxReelSymbol";
import {WixxGameConfig} from "../WixxGameConfig";
import {WixxSoundConfig} from "../WixxSoundConfig";

export class WixxStackedReelView extends BaseView {

    private static readonly SYMBOL_FADE_IN_DURATION:number = 0.1;
    private static readonly NEXT_BURST_DELAY:number = 0.4;
    private static readonly SYMBOL_LOOP_SYMBOL_DELAY:number = 0.1;

    private _backgroundLayer:PIXI.Container;
    private _stackedEffectsLayer:PIXI.Container;

    private _energyAnimation:TimelineSprite;
    private _wildSymbols:WixxReelSymbol[];
    private _blueSymbols:WixxReelSymbol[];
    private _bursts:TimelineSprite[];
    private _currentSymbols:WixxReelSymbol[];
    private _startingTimeline:TimelineLite;
    private _stackedReelTimeline:TimelineLite;

    constructor() {
        super();

        this._wildSymbols = [];
        this._blueSymbols = [];
        this._bursts = [];

        this._backgroundLayer = StageManager.getLayer(WixxGameConfig.instance.LAYERS.REEL_BACKGROUND.name);
        this._stackedEffectsLayer = StageManager.getLayer(WixxGameConfig.instance.LAYERS.STACKED_REEL_EFFECTS.name);
    }

    protected initAnimations():void {
        this._energyAnimation = new TimelineSprite(GameResources.getTextures("stackedReel4Flow"));
        this._energyAnimation.anchor.set(0.5, 0.5);
        this._energyAnimation.scale.set(1.6, 1.6);
        this._energyAnimation.x = GameConfig.instance.REEL_AREA_POS_X + GameConfig.instance.REEL_DISPLAY_POSITIONS[3][0];
        this._energyAnimation.y = GameConfig.instance.REEL_AREA_POS_Y + GameConfig.instance.REEL_AREA_HEIGHT / 2;
        this._energyAnimation.blendMode = PIXI.BLEND_MODES.ADD;
        this._energyAnimation.visible = false;
        this._backgroundLayer.addChild(this._energyAnimation);

        const symbolContainer:PIXI.Container = new PIXI.Container();
        this._stackedEffectsLayer.addChild(symbolContainer);
        const burstContainer:PIXI.Container = new PIXI.Container();
        this._stackedEffectsLayer.addChild(burstContainer);

        for(let i:number = 0; i < 3; i++) {
            const position:[number, number] = Helper.getSymbolPositions(3, i);
            const wildSym:WixxReelSymbol = new WixxReelSymbol(ReelSymbolName.WILD, 3, GameConfig.instance.SYMBOL_STATES.idle!);
            wildSym.x = position[0];
            wildSym.y = position[1];
            wildSym.visible = false;
            this._wildSymbols.push(wildSym);
            symbolContainer.addChild(wildSym);

            const blueSym:WixxReelSymbol = new WixxReelSymbol("M3_boosted", 3, GameConfig.instance.SYMBOL_STATES.idle!);
            blueSym.x = position[0];
            blueSym.y = position[1];
            blueSym.visible = false;
            this._blueSymbols.push(blueSym);
            symbolContainer.addChild(blueSym);

            const burst:TimelineSprite = new TimelineSprite(GameResources.getTextures("winBurst"));
            burst.x = position[0];
            burst.y = position[1];
            burst.blendMode = PIXI.BLEND_MODES.SCREEN;
            burst.scale.set(1.5, 1.5);
            burst.anchor.set(0.5, 0.5);
            burst.visible = false;
            this._bursts.push(burst);
            burstContainer.addChild(burst);
        }
    }

    public startStackedReel():void {
        if(this._startingTimeline == null) {
            this._startingTimeline = new TimelineLite();
            this._startingTimeline.add([
                TweenLite.fromTo(this._energyAnimation, 1, {alpha : 0}, {alpha : 1}),
                () => {
                    this._energyAnimation.playLoop();
                    this._energyAnimation.visible = true;
                },
                TweenLite.to(this, 1, {alpha : 0}), // Hide the spinning real reel.
            ]);
        } else {
            this._startingTimeline.play(0);
        }
    }

    public showStackedSymbols(feature:string):void {
        switch(feature) {
            case "BLUE_BOOSTER":
                this._currentSymbols = this._blueSymbols;
                break;
            case "RED_BLUE_BOOSTER":
            case "YELLOW_BLUE_BOOSTER":
                this._currentSymbols = this._wildSymbols;
                break;
            default:
                debugger;
                throw new Error(`WixxStackedReelView.showStackedSymbols():Invalid feature name [${feature}]`);
        }

        const timeline:TimelineLite = new TimelineLite({paused : true});
        for(let i:number = 0; i < 3; i++) {
            const burst:TimelineSprite = this._bursts[i];
            timeline.add([
                burst.getAnimation(),
                TweenLite.fromTo(this._currentSymbols[i], WixxStackedReelView.SYMBOL_FADE_IN_DURATION, {alpha : 0}, {alpha : 1}),
                () => {
                    burst.visible = true;
                    this._currentSymbols[i].visible = true;
                    if(WixxSoundConfig.instance.SYMBOL_LANDING_ON_BURST_REEL != null) {
                        SlotGame.sound.playEffect(WixxSoundConfig.instance.SYMBOL_LANDING_ON_BURST_REEL);
                    }
                }
            ], WixxStackedReelView.NEXT_BURST_DELAY * i);
        }
        timeline.add(() => this.loopSymbols(), WixxStackedReelView.NEXT_BURST_DELAY * 3);

        this._stackedReelTimeline = timeline;
        this._stackedReelTimeline.play();
    }

    private loopSymbols():void {
        const timeline:TimelineLite = new TimelineLite();
        for(let i:number = 0; i < 3; i++) {
            const symbol:WixxReelSymbol = this._currentSymbols[i];
            timeline.add(() => symbol.changeState({
                state : GameConfig.instance.SYMBOL_STATES.idle!,
                replay : true
            }), WixxStackedReelView.SYMBOL_LOOP_SYMBOL_DELAY * i);
        }

        this._stackedReelTimeline = timeline;
    }

    public stopStackedReel():void {
        // quick stop
        if(this._stackedReelTimeline && this._stackedReelTimeline.isActive()) {
            this._stackedReelTimeline.pause();
            this._stackedReelTimeline.kill();
        }

        if(this._startingTimeline && this._startingTimeline.isActive()) {
            this._startingTimeline.pause();
            this._startingTimeline.kill();
        }

        //hide the symbol animations.
        for(let i:number = 0; i < 3; i++) {
            this._bursts[i].visible = false;
            this._wildSymbols[i].visible = false;
            this._blueSymbols[i].visible = false;
        }

        // Reset the real reel.
        this.alpha = 1;

        TweenLite.to(this._energyAnimation, 0.5, {
            alpha : 0, onComplete : () => {
                this._energyAnimation.stopLoop();
                this._energyAnimation.visible = false;
            }
        });
    }
}